// NEC 2023 Table 250.102(C)(1)
// Grounding Electrode Conductor and Bonding Jumper Size for AC Systems
// Used for supply-side bonding jumpers and grounding electrode conductors

import { ConductorMaterial, COMPANY_STANDARDS } from '../types';

interface BondingEntry {
  maxSize: number;  // Largest ungrounded copper conductor size (internal representation)
  maxSizeAl: number;  // Largest ungrounded aluminum conductor size
  copperSize: number;  // Copper conductor size
  aluminumSize: number;  // Aluminum conductor size
}

// Table based on largest ungrounded service-entrance conductor or equivalent area.
// `maxSize` is the copper-conductor limit of each row; `maxSizeAl` the
// aluminum-conductor limit (the rows are defined separately per material).
const TABLE_250_102_C1: BondingEntry[] = [
  { maxSize: -2, maxSizeAl: 0, copperSize: -8, aluminumSize: -6 },        // Cu 2 or smaller / Al 1/0 or smaller
  { maxSize: 0, maxSizeAl: 0.75, copperSize: -6, aluminumSize: -4 },      // Cu 1 or 1/0 / Al 2/0 or 3/0
  { maxSize: 0.75, maxSizeAl: 250, copperSize: -4, aluminumSize: -2 },    // Cu 2/0 or 3/0 / Al 4/0 through 250 kcmil
  { maxSize: 350, maxSizeAl: 500, copperSize: -2, aluminumSize: 0 },      // Cu over 3/0 through 350 / Al over 250 through 500 kcmil
  { maxSize: 600, maxSizeAl: 900, copperSize: 0, aluminumSize: 0.75 },    // Cu over 350 through 600 / Al over 500 through 900 kcmil
  { maxSize: 1100, maxSizeAl: 1750, copperSize: 0.5, aluminumSize: 0.9 }, // Cu over 600 through 1100 / Al over 900 through 1750 kcmil
];

// Conductor cross-section in circular mils (NEC Chapter 9 Table 8), keyed by
// internal wire size. Used for the "equivalent area" of parallel sets and the
// 12.5% rule in 250.102(C)(2).
const CIRCULAR_MILS: Record<number, number> = {
  [-14]: 4110, [-12]: 6530, [-10]: 10380, [-8]: 16510, [-6]: 26240, [-4]: 41740,
  [-3]: 52620, [-2]: 66360, [-1]: 83690, [0]: 105600, [0.5]: 133100, [0.75]: 167800,
  [0.9]: 211600, [250]: 250000, [300]: 300000, [350]: 350000, [400]: 400000,
  [500]: 500000, [600]: 600000, [700]: 700000, [750]: 750000, [800]: 800000,
  [900]: 900000, [1000]: 1000000, [1250]: 1250000,
};

export function getCircularMils(wireSize: number): number {
  const cm = CIRCULAR_MILS[wireSize];
  if (cm === undefined) throw new Error(`No circular-mil area for wire size ${wireSize}`);
  return cm;
}

const SIZES_ASCENDING = Object.keys(CIRCULAR_MILS).map(Number).sort((a, b) => CIRCULAR_MILS[a] - CIRCULAR_MILS[b]);

/** Smallest conductor whose area is at least the given circular mils. */
export function findConductorForCircularMils(cmil: number): number {
  for (const size of SIZES_ASCENDING) {
    if (CIRCULAR_MILS[size] >= cmil) return size;
  }
  return SIZES_ASCENDING[SIZES_ASCENDING.length - 1];
}

/**
 * Get minimum supply-side bonding jumper or grounding electrode conductor size
 * per Table 250.102(C)(1), including Note 1 (parallel sets are treated by
 * their equivalent area) and 250.102(C)(2) (over 1100 kcmil Cu / 1750 kcmil
 * Al the jumper is at least 12.5% of the ungrounded conductor area).
 * @param largestConductor - Size of the largest ungrounded conductor per set
 * @param material - Conductor material (jumper is assumed to be the same material)
 * @param parallelSets - Number of conductors per phase in parallel (default 1)
 * @returns Required bonding conductor size
 */
export function getSupplySideBondingJumperSize(
  largestConductor: number,
  material: ConductorMaterial,
  parallelSets: number = 1
): number {
  const sets = Math.max(1, Math.floor(parallelSets));
  const equivalentCmil = getCircularMils(largestConductor) * sets;
  const tableLimitCmil = material === 'copper' ? 1100000 : 1750000;

  if (equivalentCmil > tableLimitCmil) {
    // 250.102(C)(2): 12.5% of the total ungrounded conductor area
    return findConductorForCircularMils(equivalentCmil * 0.125);
  }

  // Table lookup by equivalent area (Note 1). Entry limits of 250 and above
  // are kcmil values (350, 600, 1100) that need not be carried wire sizes.
  const limitCmil = (maxSize: number) => (maxSize >= 250 ? maxSize * 1000 : getCircularMils(maxSize));
  for (const entry of TABLE_250_102_C1) {
    if (equivalentCmil <= limitCmil(material === 'copper' ? entry.maxSize : entry.maxSizeAl)) {
      const size = material === 'copper' ? entry.copperSize : entry.aluminumSize;
      if (material === 'copper' && size < COMPANY_STANDARDS.minConductorSize) {
        return COMPANY_STANDARDS.minConductorSize;
      }
      return size;
    }
  }

  const lastEntry = TABLE_250_102_C1[TABLE_250_102_C1.length - 1];
  return material === 'copper' ? lastEntry.copperSize : lastEntry.aluminumSize;
}

/**
 * For conductors over 1100 kcmil, bonding conductor is 12.5% of conductor area
 * @param conductorArea - Area of largest conductor in circular mils
 * @returns Required bonding conductor area in circular mils
 */
export function calculateBondingForLargeConductor(conductorArea: number): number {
  return conductorArea * 0.125;
}

export const NEC_TABLE_250_102_C1_REF = {
  table: 'Table 250.102(C)(1)',
  article: '250.102(C)(1)',
  description: 'Grounding Electrode Conductor and Bonding Jumper Sizing for AC Systems',
};

export { TABLE_250_102_C1 };
